import { ElementType } from 'react';
import {
  Assessment,
  Assignment,
  CompareArrows,
  DashboardOutlined,
  Description,
  EmojiPeople,
  Explore,
  FilterList,
  MailOutline,
  LibraryBooks,
} from '@material-ui/icons';

export interface EntityCount {
  entityType: string;
  count: number;
}

interface EntityLabel {
  singular: string;
  plural: string;
}

/**
 * The entity types worth showing an admin, in display order. The copy engine
 * maps far more than this (items, fields, options), which is detail nobody
 * managing sub-accounts needs to see.
 */
const HEADLINE_ENTITY_LABELS: Array<[string, EntityLabel]> = [
  ['program', { singular: 'program', plural: 'programs' }],
  ['activity_role', { singular: 'role', plural: 'roles' }],
  ['task', { singular: 'task', plural: 'tasks' }],
  ['form', { singular: 'form', plural: 'forms' }],
  ['funnel', { singular: 'funnel', plural: 'funnels' }],
  ['report_type', { singular: 'report type', plural: 'report types' }],
  ['support_resource', { singular: 'resource', plural: 'resources' }],
  ['matching_rule', { singular: 'matching rule', plural: 'matching rules' }],
  ['communication_content', { singular: 'email', plural: 'emails' }],
  ['org_dashboard', { singular: 'organisation dashboard', plural: 'organisation dashboards' }],
];

export const headlineEntityCounts = (counts: EntityCount[]): Array<{ label: string; count: number }> => {
  const byType = new Map(counts.map((entityCount) => [entityCount.entityType, entityCount.count]));

  return HEADLINE_ENTITY_LABELS.map(([entityType, label]) => {
    const count = byType.get(entityType) ?? 0;

    return { label: count === 1 ? label.singular : label.plural, count };
  }).filter((entry) => entry.count > 0);
};

export const formatHeadlineEntityCounts = (counts: EntityCount[]): string => {
  const headline = headlineEntityCounts(counts);

  if (headline.length === 0) {
    return 'No entities copied yet';
  }

  return headline.map((entry) => `${entry.count} ${entry.label}`).join(' · ');
};

const TYPE_HEADINGS = new Map<string, string>([
  ['program', 'Programs'],
  ['activity_role', 'Roles'],
  ['task', 'Tasks'],
  ['form', 'Forms'],
  ['funnel', 'Funnels'],
  ['report_type', 'Report Types'],
  ['support_resource', 'Resources'],
  ['matching_rule', 'Matching Rules'],
  ['communication_content', 'Emails'],
  ['org_dashboard', 'Organisation Dashboard'],
]);

export const entityTypeHeading = (entityType: string): string =>
  TYPE_HEADINGS.get(entityType) ?? entityType.replace(/_/g, ' ');

const TYPE_ICONS = new Map<string, ElementType>([
  ['program', Explore],
  ['activity_role', EmojiPeople],
  ['task', Assignment],
  ['form', Description],
  ['funnel', FilterList],
  ['report_type', Assessment],
  ['support_resource', LibraryBooks],
  ['matching_rule', CompareArrows],
  ['communication_content', MailOutline],
  ['org_dashboard', DashboardOutlined],
]);

export const entityTypeIcon = (entityType: string): ElementType => TYPE_ICONS.get(entityType) ?? Assignment;
